import { AbstractInterface } from './AbstractInterface';
import { BaseApplication, FullScreenApplication, DialogApplication } from '../application';

/*
 * Life cycle functions of third party application.
 */
export class ApplicationLifeCycle extends AbstractInterface {

  /*
   * Opens a third party full screen (normal) application.
   *
   * @param {FullScreenApplication} app The instance of the application
   */
  public static openApplication(app: FullScreenApplication): void {
    this.mediator.initApplication(app);
  }

  /*
   * Opens a third party full screen (normal) application.
   *
   * @param {DialogApplication} app The instance of the application
   */
  public static openDialog(app: DialogApplication): void {
    this.onFocus(app);
  }

  /*
   * Close a third party dialog application.
   *
   * @param {DialogApplication} app - the instance the dialog application
   */
  public static closeDialog(app: DialogApplication): void {
    this.mediator.closeDialogApplication(app);
  }

  /*
   * Activate a third party dialog application when it triggers the application_ready use case
   *
   * @param {DialogApplication} app - the instance the dialog application
   */
  public static activateDialog(app: DialogApplication): void {
    this.mediator.activateDialogApplication(app);
  }

  /*
   * Reopen a third party application.
   *
   * @param {BaseApplication} app - the instance the application
   * @param urlKey {String} Key of URL map of the application, commonly set as 'default'
   */
  public static reopenApplication(app: BaseApplication, urlKey?: string): void {
    this.mediator.reInitApplication(app, urlKey);
  }

  /*
   * Life cycle: will be called on focusing the application.
   *
   * @param {BaseApplication} app - the instance the application
   */
  public static onFocus(app: BaseApplication): void {
    this.mediator.focusApplication(app);
  }

  /*
   * Life cycle: will be called on bluring the application.
   *
   * @param {BaseApplication} app - the instance the application
   */
  static onUnFocus(app: BaseApplication): void {
    this.mediator.unFocusApplication(app);
  }

  /*
   * Returns true if application is already initialized, false if initialized
   *  and not on focus or undefined if not initialized yet.
   *
   * @param {FullScreenApplication} app - the instance the application
   *
   * @return {Boolean|Undefined} true if initialized
   */
  public static isApplicationInitialized(app: FullScreenApplication): boolean {
    return this.mediator.isApplicationInitialized(app);
  }

  /*
   * Returns true if application is already initialized and focus, false if initialized
   *  and not on focus or undefined if not initialized yet.
   *
   * @param {FullScreenApplication} app - the instance the application
   *
   * @return {Boolean|Undefined} true if in focus
   */
  public static isApplicationOnFocus(app: FullScreenApplication): boolean {
    return this.mediator.isApplicationOnFocus(app);
  }

  /*
   * Returns true if dialog application is already initialized.
   * False if initialized but not ready (for dialogs with the wait_for_application_ready) option
   *
   * @param {DialogApplication} app - the instance the application
   *
   * @return {Boolean|Undefined} true if initialized
   */
  public static isDialogInitialized(app: DialogApplication): boolean {
    return this.mediator.isDialogInitialized(app);
  }

  /*
   * Returns true if dialog application is focused.
   *
   * @param {DialogApplication} app - the instance the application
   *
   * @return {Boolean|Undefined} true: initialized focused
   *                             false: initialized not focused
   */
  public static isDialogOnFocus(app: DialogApplication): boolean {
    return this.mediator.isDialogOnFocus(app);
  }
}
